import queryString from "query-string";

import { getLocalStorage, setLocalStorage } from "@/utils/localstorage";

import { Cookie } from "../../utils/cookie";

import { AnalyticsState } from "./analytics-state";
import { GUPS } from "./gups";

type XHRHeader = {
    header: string;
    value: string;
};

// This regex is the simplified RFC 5322 expression from http://www.regular-expressions.info/email.html with added %40 for encoded urls
const EMAIL_REGEX =
    /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*[@(%40)](?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/i;
const PATH_OVERRIDE = "/path-contained-email-address";

const CROSS_DOMAIN_UTM_EXPIRY = 3600000; // 1hr in ms

export class Helpers {
    /**
     * Attempts to send an HTTP request.
     * @function makeRestRequest
     * @param {string} method the request method
     * @param {string} url the request URL
     * @param {Object[]} headers an array of headers to set in the request
     * @param {string} body the body of the request
     * @param {boolean} credentials whether to send request with credentials; defaults to false
     * @param {callback} callback optional callback function
     * @param {object} mockReq used in testing so we don't actually send a request
     */
    public static makeRestRequest(
        method: string,
        url: string,
        headers: XHRHeader[],
        body: string | null,
        credentials: boolean,
        callback?: null | ((msg: string) => void),
        mockReq?: XMLHttpRequest
    ) {
        const xhr = mockReq || new XMLHttpRequest();
        xhr.open(method, url);
        xhr.withCredentials = credentials ? credentials : false;

        if (!!headers) {
            // set headers
            for (const item of headers) {
                xhr.setRequestHeader(item.header, item.value);
            }
        }

        if (!body) {
            body = null;
        }

        xhr.onreadystatechange = function () {
            if (xhr.readyState === XMLHttpRequest.DONE) {
                if (xhr.status >= 200 && xhr.status < 300) {
                    if (callback) {
                        callback(xhr.responseText);
                    }
                } else {
                    if (callback) {
                        callback("error");
                    }
                }
            }
        };
        xhr.send(body);
    }

    public static sendWrapperLoadError(message: string, data?: string) {
        const anonymousId = Cookie.readCookie("ajs_anonymous_id");
        let decodedAnonymousId;

        if (anonymousId) {
            // the cookie value is URI encoded with quotes on the outside, so decode and remove them
            decodedAnonymousId = decodeURIComponent(anonymousId).replace(
                /['"]+/g,
                ""
            );
        }
        const userId = Cookie.readCookie("__aid_user_id");

        let decodedUserId;

        if (userId) {
            decodedUserId = decodeURIComponent(userId);
        }

        const xid = Cookie.readCookie("seg_xid");

        const body = {
            anonymousId: decodedAnonymousId || null,
            userId: decodedUserId || null,
            xid: xid || null,
            message: message || null,
            version: AnalyticsState.version,
            additionalData: data || null
        };

        const env = process.env["CLIENT_LIBRARY_ENV"];
        const url =
            env === "production"
                ? "https://www.atlassian.com/endpoint/atlglobalload"
                : "https://wac.stg.internal.atlassian.com/endpoint/atlglobalload";
        const headers = [
            { header: "Content-type", value: "application/json" },
            { header: "accept", value: "*/*" }
        ];
        Helpers.makeRestRequest(
            "POST",
            url,
            headers,
            JSON.stringify(body),
            false
        );
    }

    public static getParameterByName(name: string) {
        name = name.replace(/[\[]/, "\\[").replace(/[\]]/, "\\]");
        const regex = new RegExp("[\\?&]" + name + "=([^&#]*)");
        const results = regex.exec(window.location.search);
        const value = results && results.length >= 2 ? results[1] : null;
        return !value ? "" : decodeURIComponent(value.replace(/\+/g, " "));
    }

    private static getLocalMarketingContext() {
        try {
            const localMarketingContext = getLocalStorage(
                "marketingContext",
                true
            );
            const parsedMarketingContext =
                (localMarketingContext && JSON.parse(localMarketingContext)) ||
                null;
            return parsedMarketingContext;
        } catch (error) {
            console.error(
                "Failed to parse marketingContext from localStorage:",
                error
            );
            return null;
        }
    }

    public static getUtmsFromUrl(utms: Record<string, string>) {
        if (window.location.search.includes("utm_")) {
            for (const utmKey in utms) {
                if (utms.hasOwnProperty(utmKey)) {
                    utms[utmKey] = Helpers.getParameterByName(utmKey);
                }
            }
            return utms;
        }
        return null;
    }

    private static newUtmsMatchLocalUtms(
        urlUtms: Record<string, string>,
        localUtms: Record<string, string>
    ) {
        return (
            urlUtms["utm_sfdc-campaign_id"] === localUtms["sfdcCampaignId"] &&
            urlUtms["utm_campaign"] === localUtms["campaign"] &&
            urlUtms["utm_source"] === localUtms["source"] &&
            urlUtms["utm_medium"] === localUtms["medium"] &&
            urlUtms["utm_content"] === localUtms["content"]
        );
    }

    public static getAndSetUtms(utmKeys: Record<string, string>) {
        const urlUtms = Helpers.getUtmsFromUrl(utmKeys);

        let anonymousId = Cookie.readCookie("ajs_anonymous_id");
        anonymousId =
            anonymousId &&
            decodeURIComponent(anonymousId).replace(/['"]+/g, "");

        const awcSessionId = getLocalStorage("awc.session.id", true);

        const domain = window.location.hostname;
        const localMarketingContext = Helpers.getLocalMarketingContext();

        const nullMarketingContext = JSON.stringify({
            sessionId: awcSessionId,
            domain: domain,
            lastUpdated: awcSessionId,
            utm: {
                sfdcCampaignId: "",
                campaign: "",
                source: "",
                medium: "",
                content: ""
            }
        });

        if (urlUtms) {
            if (
                !localMarketingContext ||
                (localMarketingContext &&
                    (awcSessionId !== localMarketingContext.sessionId ||
                        !Helpers.newUtmsMatchLocalUtms(
                            urlUtms,
                            localMarketingContext.utm
                        )))
            ) {
                GUPS.setMarketingContext(
                    anonymousId,
                    awcSessionId,
                    domain,
                    urlUtms
                );
            }
            return urlUtms;
        } else {
            if (localMarketingContext) {
                const localUtms = {
                    "utm_sfdc-campaign_id":
                        localMarketingContext.utm.sfdcCampaignId,
                    "utm_campaign": localMarketingContext.utm.campaign,
                    "utm_source": localMarketingContext.utm.source,
                    "utm_medium": localMarketingContext.utm.medium,
                    "utm_content": localMarketingContext.utm.content
                };

                if (localMarketingContext.sessionId === null) {
                    const hasUtms = Object.values(localUtms).some(
                        (value) => value !== ""
                    );
                    if (hasUtms) {
                        GUPS.setMarketingContext(
                            anonymousId,
                            awcSessionId,
                            domain,
                            localUtms
                        );
                    } else {
                        GUPS.getMarketingContext(
                            anonymousId,
                            awcSessionId,
                            domain,
                            awcSessionId,
                            nullMarketingContext
                        );
                    }
                } else if (domain === localMarketingContext.domain) {
                    if (localMarketingContext.sessionId === awcSessionId) {
                        return localUtms;
                    } else {
                        GUPS.getMarketingContext(
                            anonymousId,
                            awcSessionId,
                            domain,
                            localMarketingContext.lastUpdated,
                            nullMarketingContext
                        );
                    }
                } else {
                    const validCrossDomainUtms =
                        awcSessionId &&
                        Number(awcSessionId) -
                            Number(localMarketingContext.lastUpdated) <
                            CROSS_DOMAIN_UTM_EXPIRY;
                    if (validCrossDomainUtms) {
                        // overwrite session.id and domain in marketingContext to enable single domain logic on subsequent data fetches
                        setLocalStorage(
                            "marketingContext",
                            JSON.stringify({
                                sessionId: awcSessionId,
                                domain: domain,
                                lastUpdated: awcSessionId,
                                utm: localMarketingContext.utm
                            })
                        );
                        return localUtms;
                    }
                }
            } else {
                GUPS.getMarketingContext(
                    anonymousId,
                    awcSessionId,
                    domain,
                    awcSessionId,
                    nullMarketingContext
                );
            }
        }
        setLocalStorage("marketingContext", nullMarketingContext);
        return null;
    }

    // taken from https://github.com/segmentio/canonical/blob/master/lib/index.js
    public static canonical() {
        const tags = Array.from(document.getElementsByTagName("link"));
        for (const tag of tags) {
            if (tag && tag.getAttribute("rel") === "canonical") {
                return tag.getAttribute("href");
            }
        }
        return null;
    }

    // taken from https://github.com/segmentio/analytics.js-core/blob/master/lib/pageDefaults.js
    public static getCanonicalUrl() {
        const canon = Helpers.canonical();
        if (canon) return canon;
        const url = window.location.href;
        const i = url.indexOf("#");
        return i === -1 ? url : url.slice(0, i);
    }

    public static addActualUrl(obj: Record<string, unknown>) {
        if (window.location.href !== Helpers.getCanonicalUrl()) {
            // respect cleaned url
            if (obj["url"]) {
                obj["actualUrl"] = obj["url"];
            } else {
                obj["actualUrl"] = Helpers.maskURL(window.location.href);
            }
        }
    }

    public static isObject(o: unknown): o is object {
        return (
            o !== null && typeof o === "object" && Array.isArray(o) === false
        );
    }

    public static urlContainsEmail(url: string) {
        return Boolean(EMAIL_REGEX.exec(decodeURIComponent(url)));
    }

    // dropping attributes containing email
    public static dropEmailQueryParams(obj: Record<string, string>) {
        const sanitizedObj: Record<string, string> = {};
        Object.entries(obj).forEach(([key, value]) => {
            if (!Helpers.urlContainsEmail(value)) {
                sanitizedObj[key] = value;
            }
        });
        return sanitizedObj;
    }

    // mask search terms
    public static maskSearchTerms(obj: Record<string, string>) {
        const sanitizedObj: Record<string, string> = {};
        Object.entries(obj).forEach(([key, value]) => {
            if (key === "term" || key === "q") {
                sanitizedObj[key] = "[REDACTED]";
            } else {
                sanitizedObj[key] = value;
            }
        });
        return sanitizedObj;
    }

    // dropping email from the url path
    public static dropEmailFromPath(path: string) {
        let newPath = "";
        if (Helpers.urlContainsEmail(path)) {
            const pathArray = path.split("/");
            pathArray.forEach(function (value) {
                if (value !== "" && !Helpers.urlContainsEmail(value)) {
                    newPath = newPath + "/" + value;
                }
            });
        }
        return newPath === "" ? path : newPath;
    }

    // convert query params object to query params string
    public static convertQueryObjToString(obj: Record<string, string>) {
        let queryString = new URLSearchParams(obj).toString();
        if (queryString !== "") {
            queryString = "?" + queryString;
        }
        return queryString;
    }

    public static emailAddressIsDetected() {
        return (
            Helpers.urlContainsEmail(window.location.href) ||
            Helpers.urlContainsEmail(document.referrer)
        );
    }

    public static hostnameFromUrl(url: string) {
        return url.replace(/http(s|):\/\//g, "").split(/(\/|:)/g)[0];
    }

    public static addCleanedUrlAndPaths(obj: Record<string, unknown>) {
        obj["url"] = window.location.origin + PATH_OVERRIDE;
        obj["path"] = PATH_OVERRIDE;
        obj["search"] = "";
        return obj;
    }

    public static addCleanedReferrer(obj: Record<string, unknown>) {
        obj["referrer"] =
            Helpers.urlWithoutPath(document.referrer) + PATH_OVERRIDE;
        return obj;
    }

    public static urlWithoutPath(url: string) {
        const fakeTag = document.createElement("a");
        fakeTag.href = url;
        return fakeTag.protocol + "//" + fakeTag.host;
    }

    /**
     * Urls containing email address in url path or query params are dropped and
     * search term query params are masked.
     * Search terms (and all UGC) should no longer be tracked.
     */
    public static maskURL(input: string) {
        if (input === "" || input === undefined) {
            return input;
        }
        const url = new URL(input);
        let obj = queryString.parse(url.search) as Record<string, string>;
        obj = Helpers.dropEmailQueryParams(obj);
        if (url.pathname.includes("/search")) {
            obj = Helpers.maskSearchTerms(obj);
        }

        return (
            url.origin +
            Helpers.dropEmailFromPath(url.pathname) +
            Helpers.convertQueryObjToString(obj)
        );
    }
}
